import {
  EmploymentStatus,
  Occupation,
  UpdateProfileStatus,
  useCustomerDetails,
  useUpdateEmploymentInfo,
} from '@egw/api'
import { Button, Field, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { Controller, useForm } from 'react-hook-form'
import { type FC, useEffect, useState } from 'react'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { EmploymentField } from '../../types'

const log = new Logger('Update Employment Status')

interface EmploymentStatusFormInterface {
  [EmploymentField.EMPLOYMENT_STATUS]: EmploymentStatus
  [EmploymentField.OCCUPATION]: Occupation
}

export const EditEmploymentStatusField: FC = () => {
  const { t } = useTranslation('features-settings')
  const { isOpen, openModal, closeModal } = useModalState()
  const [updateError, setUpdateError] = useState<boolean>(false)

  const { data, refetch, isRefetching } = useCustomerDetails()

  const employmentStatus = data?.customer.employmentStatus
  const occupation = data?.customer.occupation

  const onClose = () => {
    setUpdateError(false)
    closeModal()
  }

  const { mutate: updateEmploymentInfo, isLoading } = useUpdateEmploymentInfo({
    onSuccess: (res) => {
      if (res.__typename === 'UpdateProfileError' || res.status === UpdateProfileStatus.FAILED) {
        log.error('Error updating employment status', { data })
        setUpdateError(true)
      } else if (res.status === UpdateProfileStatus.COMPLETE) {
        void refetch()
          .catch((error: unknown) => {
            log.error(error)
          })
          .finally(() => {
            onClose()
            reset()
          })
      }
    },
    onError: () => {
      setUpdateError(true)
    },
  })

  const defaultValues = {
    [EmploymentField.EMPLOYMENT_STATUS]: employmentStatus,
    [EmploymentField.OCCUPATION]: occupation,
  }

  const { handleSubmit, control, reset, formState, watch } = useForm<EmploymentStatusFormInterface>({
    mode: 'all',
    defaultValues,
  })

  useEffect(() => {
    if (data) {
      reset({
        [EmploymentField.EMPLOYMENT_STATUS]: employmentStatus,
        [EmploymentField.OCCUPATION]: occupation ?? undefined,
      })
    }
  }, [data, reset, employmentStatus, occupation])

  const onSubmit = (formData: EmploymentStatusFormInterface): void => {
    setUpdateError(false)
    log.debug('Submitting Form', { formData })
    void updateEmploymentInfo(formData)
  }

  const employmentStatusField = watch([EmploymentField.EMPLOYMENT_STATUS])[0]
  const showOccupation =
    employmentStatusField === EmploymentStatus.EMPLOYED || employmentStatusField === EmploymentStatus.INDEPENDENT

  useEffect(() => {
    if (!showOccupation) {
      reset({
        [EmploymentField.EMPLOYMENT_STATUS]: employmentStatusField,
        [EmploymentField.OCCUPATION]: undefined,
      })
    }
  }, [showOccupation, reset, employmentStatusField])

  return employmentStatus ? (
    <>
      <Field
        valueClassName="font-light"
        className="w-full justify-between border-list mb-0 py-4 px-2"
        onEdit={openModal}
        value={t('employment_labels.employment_status')}
        rightValue={t(`employment_status.${employmentStatus}`)}
      />
      <ModalDrawer title={t('employment_labels.employment_status')} isOpen={isOpen} onClose={onClose}>
        <form
          id="updateEmploymentStatusForm"
          className="flex flex-col gap-4 h-full md:h-screen justify-between"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit(onSubmit)()
          }}
        >
          <div className="flex flex-col gap-10">
            <div>
              <p className="mb-4 mt-4 font-semibold">{t('update_employment_status.your_employment_status')}</p>
              <Controller
                name={EmploymentField.EMPLOYMENT_STATUS}
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    value={field.value}
                    options={Object.values(EmploymentStatus).map((item) => {
                      return {
                        label: t(`employment_status.${item}`),
                        value: item,
                      }
                    })}
                  />
                )}
              />
            </div>
            {showOccupation ? (
              <div>
                <p className="mb-4 font-semibold">{t('update_occupation.your_occupation')}</p>
                <Controller
                  name={EmploymentField.OCCUPATION}
                  control={control}
                  rules={{
                    required: { value: showOccupation, message: t('default.required') },
                  }}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      value={field.value}
                      options={Object.values(Occupation).map((item) => {
                        return {
                          label: t(`occupation.${item}`),
                          value: item,
                        }
                      })}
                    />
                  )}
                />
              </div>
            ) : null}
            {updateError ? (
              <div className="flex bg-alertErrorBg p-3 rounded-md">
                <InformationCircleIcon className="w-6 h-6 fill-red-500" />
                <p className="text-sm text-alertErrorText ml-2">{t('common.update_error')} </p>
              </div>
            ) : null}
          </div>
          <Button
            id="updateEmploymentStatus"
            type="submit"
            isLoading={isLoading || isRefetching}
            isDisabled={!formState.isValid}
            className="mt-4 md:mt-0"
          >
            {t('common.confirm')}
          </Button>
        </form>
      </ModalDrawer>
    </>
  ) : null
}
